import { useDictionary } from '@/lib/context/locale-provider';
import { convertLanguageName } from '@/lib/convert-language-code';
import { Languages } from '@/lib/dictionaries';
import { getShopMissionsToShow } from '@/lib/get-shop-mission';
import { isDonationShop } from '@/lib/shipping-fee';
import { DONATION_AMOUNT_TEXT_OVERRIDES } from '@/lib/shop-donation-overrides';
import { IShop } from '@/schema/shop';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import classes from './shop-list.module.scss';

function ShopItem({
  lang,
  shop,
  index,
}: {
  lang: Languages;
  shop: IShop;
  index: number;
}) {
  const dict = useDictionary();

  const now = new Date();
  const isEnded = new Date(shop.endAt) <= now;
  const isDonation = isDonationShop(shop.category);

  const missionsToShow = React.useMemo(
    () => getShopMissionsToShow(shop.missions, shop.salesCount),
    [shop.missions, shop.salesCount],
  );

  const progressHeight = React.useMemo(() => {
    let progress = 0;

    missionsToShow.forEach(mission => {
      if (shop.salesCount >= mission.section) {
        progress += 50;
      } else {
        progress += (shop.salesCount / mission.section) * 50;
      }
    });

    return `${progress}%`;
  }, [missionsToShow, shop.salesCount]);

  const formatNumber = (n: number | undefined) =>
    (n ?? 0).toLocaleString('en-US');

  if (!dict) {
    return null;
  }

  return (
    <li>
      <Link
        href={`/${lang}/shop/${shop.slug[lang]}`}
        prefetch={true}
        className={classes.itemWrap}
      >
        <div className={classes.imgBox}>
          <Image
            src={shop.thumbnail.url}
            alt={shop.title[lang]}
            width={400}
            height={400}
            priority={index < 4}
          />
          {shop.groupOrderTitle && (
            <span className={classes.badge}>{shop.groupOrderTitle}</span>
          )}

          {isEnded && (
            <>
              <div className={classes.overlay}></div>
              <div className={classes.overlayTitle}>
                <div>{dict!.common.end}</div>
              </div>
            </>
          )}
        </div>
        <div className={classes.infoBox}>
          <div>
            <div className={classes.subjectBox}>{shop.title[lang]}</div>
            {!shop.isQuantityDisabled && (
              <div className={classes.cntBox}>
                {(isDonation
                  ? dict!.shop.realTimeDonationRaised
                  : dict!.shop.realTimeSoldCount
                ).replace(
                  '{n}',
                  isDonation && DONATION_AMOUNT_TEXT_OVERRIDES[shop.productKey]
                    ? DONATION_AMOUNT_TEXT_OVERRIDES[shop.productKey]
                    : formatNumber(shop.salesCount),
                )}
              </div>
            )}
          </div>
          {!isDonation && (
            <div
              className={`${classes.goalBox} ${shop.missions.length > 0 ? classes.hasGoal : undefined} ${shop.isQuantityDisabled ? classes.goalBoxExpanded : undefined}`}
            >
              {shop.missions.length > 0 && (
                <div className={classes.barWrap}>
                  <div className={classes.barBox}>
                    <div
                      className={classes.bar}
                      style={{
                        height: progressHeight,
                      }}
                    ></div>
                  </div>
                  <div className={classes.goalList}>
                    {shop.missions.length > 0 &&
                      missionsToShow.map((mission, index: number) => (
                        <div
                          className={classes.goalItem}
                          key={`${shop.id}-${mission.partner.id}-${index}`}
                        >
                          <div className={classes.partBox}>
                            Part {mission.number}
                          </div>
                          <div className={classes.txtBox}>
                            {mission.partner.title[convertLanguageName(lang)]}
                          </div>
                          <div className={classes.labelBox}>
                            <span
                              className={`${classes.labelItem} ${shop.salesCount >= mission.section && classes.active}`}
                            >
                              {shop.salesCount >= mission.section
                                ? dict!.shop.goalCounting.replace(
                                    '{n}',
                                    formatNumber(mission.section),
                                  )
                                : dict!.shop.inProgress}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className={classes.priceBox}>
            <span className={classes.price}>
              {lang === 'ko' ? (
                <span>{formatNumber(shop.minPrice)}</span>
              ) : lang === 'ja' ? (
                <span>
                  {shop.minPriceJpy ? formatNumber(shop.minPriceJpy) : 0}
                </span>
              ) : (
                <span>
                  {shop.minPriceUsd ? formatNumber(shop.minPriceUsd) : 0}
                </span>
              )}
              {dict!.common.currency} ~
            </span>
          </div>
        </div>
      </Link>
    </li>
  );
}

export default ShopItem;
